/**
 * Admin-only OSM tag → Apple Wallet category map.
 * Never shipped to user devices; import + editor only.
 */

import mapFile from "../../catalog/merchant-category-map.json";
import type { WalletCategory } from "./catalogTypes";
import {
  defaultGranularKind,
  normalizeMerchantName,
  normalizeWalletCategory,
} from "./catalogTypes";

export type OsmTagMapping = {
  osm: string;
  walletCategory: WalletCategory;
  granularKind: string;
};

export type ForcedBrandMapping = {
  normalizedName: string;
  displayName?: string;
  aliases: string[];
  walletCategory: WalletCategory;
  granularKind: string;
};

type RawMap = {
  tags?: Array<{ osm?: string; walletCategory?: string; granularKind?: string }>;
  forced?: Array<{
    normalizedName?: string;
    displayName?: string;
    aliases?: string[];
    walletCategory?: string;
    granularKind?: string;
  }>;
};

const raw = mapFile as RawMap;

function asWallet(raw: string | undefined, fallback: WalletCategory): WalletCategory {
  return normalizeWalletCategory(raw) ?? fallback;
}

export const OSM_TAG_MAPPINGS: OsmTagMapping[] = (raw.tags ?? [])
  .map((row) => {
    const osm = (row.osm ?? "").trim();
    if (!osm) return null;
    const walletCategory = asWallet(row.walletCategory, "other");
    return {
      osm,
      walletCategory,
      granularKind: (row.granularKind ?? osm).trim() || osm,
    } satisfies OsmTagMapping;
  })
  .filter((row): row is OsmTagMapping => Boolean(row));

const TAG_BY_OSM = new Map(OSM_TAG_MAPPINGS.map((row) => [row.osm.toLowerCase(), row]));

export const MAPPED_OSM_TAGS = new Set(TAG_BY_OSM.keys());

export const FORCED_BRAND_MAPPINGS: ForcedBrandMapping[] = [];
for (const row of raw.forced ?? []) {
  const normalizedName = normalizeMerchantName(row.normalizedName);
  if (!normalizedName) continue;
  const walletCategory = asWallet(row.walletCategory, "other");
  FORCED_BRAND_MAPPINGS.push({
    normalizedName,
    displayName: row.displayName?.trim() || undefined,
    aliases: (row.aliases ?? []).map((alias) => alias.trim().toLowerCase()).filter(Boolean),
    walletCategory,
    granularKind: (row.granularKind ?? "").trim() || defaultGranularKind(walletCategory),
  });
}

const FORCED_BY_NAME = new Map(FORCED_BRAND_MAPPINGS.map((row) => [row.normalizedName, row]));

export function mappingForOsmTag(osm: string | null | undefined): OsmTagMapping {
  const tag = (osm ?? "").trim().toLowerCase();
  if (!tag) {
    return { osm: "cms/other", walletCategory: "other", granularKind: "cms/other" };
  }
  return (
    TAG_BY_OSM.get(tag) ?? {
      osm: tag,
      walletCategory: "other",
      granularKind: tag,
    }
  );
}

export function forcedMappingForName(
  name: string | null | undefined,
  aliases: readonly string[] = [],
): ForcedBrandMapping | null {
  const keys = [normalizeMerchantName(name), ...aliases.map((alias) => normalizeMerchantName(alias))];
  for (const key of keys) {
    const hit = FORCED_BY_NAME.get(key);
    if (hit) return hit;
  }
  return null;
}

/** NSI category path `brands/amenity/fast_food` → `amenity/fast_food`. */
export function osmTagFromNsiPath(path: string | null | undefined): string | null {
  const rawPath = (path ?? "").trim().toLowerCase();
  if (!rawPath) return null;
  const parts = rawPath.split("/").filter(Boolean);
  if (parts.length < 2) return null;
  const tree = parts[0];
  if (tree === "brands" || tree === "operators" || tree === "transit") {
    return parts.slice(1).join("/");
  }
  if (parts.length >= 2) return `${parts[0]}/${parts[1]}`;
  return null;
}

export function classifyOsmTag(osm: string | null | undefined): {
  walletCategory: WalletCategory;
  granularKind: string;
} {
  const mapped = mappingForOsmTag(osm);
  return { walletCategory: mapped.walletCategory, granularKind: mapped.granularKind };
}
